/**
 * DrillSettingsView.js
 *
 * Full-screen settings view for Drills module configuration.
 * WBC3 Edition: 5 settings for drill management
 *
 * Settings:
 * 1. Default Drill Duration - 15-120 minutes
 * 2. Due Soon Warning Days - 1-30 days
 * 3. Company Required Drills - Additional drills beyond WBC3 minimums
 * 4. Require Notes for Failed Drills - WBC3 compliance
 * 5. Enable Onboard Training Tab
 *
 * Pattern: Full-screen overlay accessed via gear icon (same as DefectsSettingsView)
 */

import React, { useState } from 'react';
import {
  Clock,
  Bell,
  Shield,
  FileText,
  BookOpen,
  Info,
  RotateCcw,
  Check,
  AlertTriangle
} from 'lucide-react';
import { useDrillsConfig, OPTIONAL_DRILLS } from '../../../../hooks/domain/drills/useDrillsConfig';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

/**
 * Section wrapper component
 */
const SettingsSection = ({ icon: Icon, title, description, children, regulatory = false }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="bg-gray-900 px-5 py-3">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-cyan-400" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-white">{title}</h3>
            {regulatory && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-500 text-white rounded">
                WBC3
              </span>
            )}
          </div>
          {description && (
            <p className="text-sm text-slate-300 mt-0.5">{description}</p>
          )}
        </div>
      </div>
    </div>
    <div className="p-5">
      {children}
    </div>
  </div>
);

/**
 * Toggle switch component
 */
const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`
      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
      border-2 border-transparent transition-colors duration-200 ease-in-out
      focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2
      ${enabled ? 'bg-cyan-600' : 'bg-slate-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-5 w-5 transform rounded-full
        bg-white shadow ring-0 transition duration-200 ease-in-out
        ${enabled ? 'translate-x-5' : 'translate-x-0'}
      `}
    />
  </button>
);

/**
 * Number input with range
 */
const NumberInput = ({ value, onChange, min, max, unit = 'days' }) => {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    const clamped = Math.min(max, Math.max(min, localValue));
    setLocalValue(clamped);
    if (clamped !== value) {
      onChange(clamped);
    }
  };

  // Sync when external value changes
  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <div className="flex items-center gap-3">
      <input
        type="number"
        value={localValue}
        onChange={(e) => setLocalValue(parseInt(e.target.value) || min)}
        onBlur={handleBlur}
        min={min}
        max={max}
        className="w-20 px-3 py-2 text-sm border border-slate-300 rounded-lg
          focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
      />
      <span className="text-sm text-slate-500">{unit}</span>
      <span className="text-xs text-slate-400">({min}-{max})</span>
    </div>
  );
};

/**
 * Company drill toggle row with frequency input
 */
const DrillToggleRow = ({ drill, enabled, frequency, onToggle, onFrequencyChange }) => (
  <div className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
    <div className="flex items-center gap-3 flex-1">
      <Toggle enabled={enabled} onChange={onToggle} />
      <div>
        <span className="text-sm font-medium text-slate-700">{drill.name}</span>
        {drill.recommended && (
          <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
            Recommended
          </span>
        )}
      </div>
    </div>
    {enabled && (
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Every</span>
        <input
          type="number"
          value={frequency || drill.defaultFrequency}
          onChange={(e) => onFrequencyChange(parseInt(e.target.value) || drill.defaultFrequency)}
          min={1}
          max={365}
          className="w-16 px-2 py-1 text-sm border border-slate-300 rounded
            focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
        />
        <span className="text-xs text-slate-500">days</span>
      </div>
    )}
  </div>
);

/**
 * Main DrillSettingsView component
 */
const DrillSettingsView = ({ onBack }) => {
  // FT-356c: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  const {
    defaultDrillDuration,
    dueSoonWarningDays,
    companyRequiredDrills,
    drillFrequencies,
    requireNotesForFailedDrills,
    enableTrainingSection,
    setDefaultDrillDuration,
    setDueSoonWarningDays,
    toggleCompanyDrill,
    setDrillFrequency,
    setRequireNotesForFailedDrills,
    setEnableTrainingSection,
    resetToDefaults,
    lastModified,
    modifiedBy
  } = useDrillsConfig();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Show feedback message
  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  // Handle reset
  const handleReset = async () => {
    await resetToDefaults();
    setShowResetConfirm(false);
    showFeedback('Settings reset to defaults');
  };

  // Count enabled company drills
  const enabledCount = companyRequiredDrills?.length || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Drill Settings</h1>
                <p className="text-sm text-slate-500">Configure drill requirements for this vessel</p>
              </div>
            </div>

            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600
                hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div className={`
          fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg
          ${feedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
        `}>
          <div className="flex items-center gap-2">
            {feedback.type === 'success' ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            {feedback.message}
          </div>
        </div>
      )}

      {/* Reset Confirmation */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset to Defaults?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  This will reset all drill settings to their default values, including removing all company-required drills. This action is logged.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Reset Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Audit Notice */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Audit Trail Active</p>
            <p className="mt-0.5">
              All changes are logged with your name and timestamp for SMS compliance.
            </p>
          </div>
        </div>

        {/* Mandatory Drills Notice */}
        <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">WBC3 Mandatory Drills</p>
            <p className="mt-0.5">
              Fire and Abandon Ship drills are mandatory <strong>monthly</strong> per SI 1999/2722.
              Man Overboard drills are required <strong>quarterly</strong> per WBC3 14.11.2.
              These cannot be disabled.
            </p>
          </div>
        </div>

        {/* Section 1: Default Drill Duration */}
        <SettingsSection
          icon={Clock}
          title="Default Drill Duration"
          description="Set the default duration for new drill records"
        >
          <div>
            <p className="text-sm text-slate-600 mb-3">
              New drill records will use this as the default duration. Can be changed when recording.
            </p>
            <NumberInput
              value={defaultDrillDuration}
              onChange={setDefaultDrillDuration}
              min={15}
              max={120}
              unit="minutes"
            />
          </div>
        </SettingsSection>

        {/* Section 2: Due Soon Warning */}
        <SettingsSection
          icon={Bell}
          title="Due Soon Warning"
          description="When to show upcoming drill warnings"
        >
          <div>
            <p className="text-sm text-slate-600 mb-3">
              Show "Due Soon" warning when a drill is due within this many days.
            </p>
            <NumberInput
              value={dueSoonWarningDays}
              onChange={setDueSoonWarningDays}
              min={1}
              max={30}
              unit="days"
            />
          </div>
        </SettingsSection>

        {/* Section 3: Company Required Drills */}
        <SettingsSection
          icon={Shield}
          title="Company Required Drills"
          description={`${enabledCount} additional drills enabled beyond WBC3 minimums`}
        >
          <p className="text-sm text-slate-600 mb-4">
            Select additional drills to make mandatory for your vessel's SMS. These will be tracked in compliance calculations.
          </p>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {OPTIONAL_DRILLS.map(drill => (
              <DrillToggleRow
                key={drill.type}
                drill={drill}
                enabled={companyRequiredDrills?.includes(drill.type)}
                frequency={drillFrequencies?.[drill.type]}
                onToggle={(enabled) => toggleCompanyDrill(drill.type, enabled)}
                onFrequencyChange={(days) => setDrillFrequency(drill.type, days)}
              />
            ))}
          </div>
        </SettingsSection>

        {/* Section 4: Require Notes for Failed Drills */}
        <SettingsSection
          icon={FileText}
          title="Require Notes for Failed Drills"
          description="WBC3 requires documented corrective actions for drill deficiencies"
          regulatory={true}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Enforce Notes Documentation</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, users must document issues and corrective actions when recording
                a drill with a Failed or Partial outcome.
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Regulatory Note:</strong> WBC3 Section 14.11 requires that drill deficiencies
                  are documented with corrective actions. Disabling this setting may affect audit compliance.
                </p>
              </div>
            </div>
            <Toggle
              enabled={requireNotesForFailedDrills}
              onChange={setRequireNotesForFailedDrills}
            />
          </div>
        </SettingsSection>

        {/* Section 5: Enable Training Section */}
        <SettingsSection
          icon={BookOpen}
          title="Onboard Training Tab"
          description="Track training sessions separately from drills"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Enable Onboard Training Section</p>
              <p className="text-sm text-slate-500 mt-0.5">
                When enabled, shows a separate tab for tracking onboard training sessions
                and competency assessments.
              </p>
            </div>
            <Toggle
              enabled={enableTrainingSection}
              onChange={setEnableTrainingSection}
            />
          </div>
        </SettingsSection>

        {/* Last Modified */}
        {lastModified && (
          <div className="text-xs text-slate-500 text-center pt-4">
            Last modified: {new Date(lastModified).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
            {modifiedBy?.name && ` by ${modifiedBy.name}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default DrillSettingsView;
